import * as React from "react";
import {fetchSearchSuggestions} from "../../store/api/search/searchApi";
import {SearchInput} from "./searchInput/SearchInput";
import {useSearchDispatch, useSearchSelector} from "../../store/slices/search/searchSlice";
import {getSearchResults} from "../../store/slices/search/actions";
import {usePlayerDispatch} from "../../store/slices/player/playerSlice";
import {updateNowPlaying} from "../../store/slices/player/actions";
import {fetchPlaylist} from "../../store/api/playlist/playlistApi";
import {MediaStrip} from "../common/mediaStrip/MediaStrip";
import {SkeletonStrip} from "../common/skeleton/SkeletonStrip";
import {FeaturedContent, MediaItem} from "../../store/api/types";
import {track} from "../../utils/telemetry";

import styles from './Search.module.scss';

export const Search: React.FC = () => {
    const searchDispatch = useSearchDispatch();
    const playerDispatch = usePlayerDispatch();
    const {isLoading, searchResults} = useSearchSelector(['isLoading', 'searchResults']);
    const [suggestions, setSuggestions] = React.useState<string[]>([]);

    const resultItems = React.useMemo(() => (searchResults ?? []).map(sr => ({name: sr.type, items: sr.media} as FeaturedContent))
    , [searchResults])

    const handleSongClick = React.useCallback(async (item: MediaItem) => {
        void track('search_result_click', {resultType: 'Songs'});
        const response = await fetchPlaylist(item.id);
        if (!response.ok) return;
        const playlist = response.value;
        playerDispatch(updateNowPlaying({
            nowPlaying: {song: playlist.tracks[0], playlist},
            isInitiatedByPlayer: false,
            source: 'search',
        }));
    }, [playerDispatch]);

    const handleInputChanged = async (term: string) => {
        try {
            const results = await fetchSearchSuggestions(term.toLowerCase().trim().replace("'", ''));
            setSuggestions(results)
        } catch (_e) {
            console.log('Suggestions failed')
        }
    }

    const handleSubmit = (term: string) => {
        // GA4 caps string params at 100 chars; lowercase+trim normalizes dashboard grouping.
        void track('search_submit', {query: term.toLowerCase().trim().slice(0, 100)});
        searchDispatch(getSearchResults(term));
    }

    return (
        <div>
            <SearchInput items={suggestions} onChange={handleInputChanged} onSubmit={handleSubmit} />
            <div className={styles.searchResults}>
            {isLoading ? (
                <>{[0, 1, 2].map(i => <SkeletonStrip key={i} />)}</>

            ) : (
                <div className="search__content">
                    {resultItems.map(resultItem =>
                        <MediaStrip
                            key={resultItem.name}
                            featuredContent={resultItem}
                            onItemClick={resultItem.name === 'Songs' ? handleSongClick : undefined}
                        />
                    )}
                </div>
            )}
            </div>
        </div>
    );
}
